import { CategorizedItem } from '../services/reportsApi';

// Canonical badge labels, ordered most→least actionable for display/sort.
// Keep in sync with getClassificationBadge in ReportDetails.tsx and the
// .item-type-badge classes in styles/components/badges.css. A new backend
// classification only surfaces here if getItemBadgeType is also taught to
// return it — the two must be updated together.
export const BADGE_TYPE_ORDER = [
  'Defect',
  'Needs Contractor Evaluation',
  'Maintenance',
  'Observation',
  'Upgrade',
  'Limitation',
  'Service',
  'Further Inspection',
  'Info',
  'Finding',
] as const;

export type BadgeType = typeof BADGE_TYPE_ORDER[number];

// Maps a badge label to its CSS modifier class (badges.css).
const BADGE_CLASS: Record<BadgeType, string> = {
  'Defect': 'defect',
  'Needs Contractor Evaluation': 'contractor-eval',
  'Maintenance': 'maintenance',
  'Observation': 'observation',
  'Upgrade': 'upgrade',
  'Limitation': 'limitation',
  'Service': 'service',
  'Further Inspection': 'needs-further-inspection',
  'Info': 'informational',
  'Finding': 'unclassified',
};

export function getBadgeClassName(badgeType: BadgeType): string {
  return BADGE_CLASS[badgeType];
}

// Resolves the single per-finding badge label. MUST match the branch order in
// ReportDetails.getClassificationBadge exactly: the contractor-eval flag wins,
// then non-defect itemType, then complianceType for *priced* defects only
// (reports processed before the unreviewed defaults changed carry
// 'Observation' on every unreviewed item, so reading complianceType
// unconditionally would mislabel non-defects and unpriced defects; newer
// reports carry null there, which falls through to 'Defect').
export function getItemBadgeType(item: CategorizedItem): BadgeType {
  if (item.needsContractorEvaluation === true) return 'Needs Contractor Evaluation';
  if (item.itemType === 'limitation') return 'Limitation';
  if (item.itemType === 'informational') return 'Info';
  if (item.itemType === 'needs_further_inspection') return 'Further Inspection';
  if (item.itemType === 'service') return 'Service';
  if (item.itemType === 'defect') {
    // complianceType is meaningful only for a priced (compliance-reviewed) defect.
    if (typeof item.estimatedCost === 'number' && item.estimatedCost > 0) {
      switch (item.complianceType) {
        case 'Defect': return 'Defect';
        case 'Upgrade': return 'Upgrade';
        case 'Maintenance': return 'Maintenance';
        case 'Observation': return 'Observation';
        default: return 'Defect';
      }
    }
    return 'Defect';
  }
  return 'Finding'; // typeless legacy item
}

// Badge types actually present in a list, returned in BADGE_TYPE_ORDER order.
export function getAvailableBadgeTypes(items: CategorizedItem[]): BadgeType[] {
  if (!items || items.length === 0) return [];
  const present = new Set(items.map(getItemBadgeType));
  return BADGE_TYPE_ORDER.filter(t => present.has(t));
}
